import { useEffect, useRef, ChangeEventHandler, useState, FC } from 'react';
import { useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import {
    Box,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ConditionalStyles, TableColumn } from 'react-data-table-component';
import { Quote, OpenOrder, ShippedOrder } from '../types.d';
import { useAxiosWrapper } from '../helpers/axios-wrapper';
import {
    errorState,
    quotesState,
    openOrdersState,
    shippedOrdersState,
    currentQuoteState,
    userState,
} from '../state/state';
import { IdolTimerWrapper } from '../components/IdolTimerWrapper';
import ResizableTable from '../components/ResizableTable';
import {
    TempcoButton,
    TempcoCard,
    TempcoTextField,
    TempcoHeading,
    TempcoBodyText,
    TempcoAlert,
    TempcoGradientText,
    TempcoContainer,
    tempcoColors,
} from '../components/TempcoUI';
import '../styles/custom-datatable.css';

// Create a styled wrapper component to force styling
const StyledDataTableWrapper = ({ children }: { children: React.ReactNode }) => (
    <div 
        className="custom-reversed-table"
        style={{
            // CSS-in-JS to ensure styles apply
            '--normal-bg': 'rgba(88, 147, 223, 0.15)',
            '--hover-bg': 'rgba(36, 52, 77, 0.8)',
            '--striped-bg': 'rgba(88, 147, 223, 0.25)',
        } as React.CSSProperties}
    >
        <style>
          {`
            .custom-reversed-table [class*="rdt_TableHeadRow"] {
              background-color: rgba(237, 28, 36, 0.3) !important;
            }
            .custom-reversed-table [class*="rdt_TableCol"] {
              color: #000000 !important;
              font-size: 16px !important;
              font-weight: bold !important;
              font-family: 'Oswald', 'Arial Black', 'Arial', sans-serif !important;
            }
            .custom-reversed-table [class*="rdt_TableRow"] {
              background-color: rgba(88, 147, 223, 0.15) !important;
              color: #000000 !important;
            }
            .custom-reversed-table [class*="rdt_TableRow"]:hover {
              background-color: rgba(36, 52, 77, 0.8) !important;
              color: #000000 !important;
            }
            .custom-reversed-table [class*="rdt_TableRow"]:nth-child(odd) {
              background-color: rgba(88, 147, 223, 0.25) !important;
            }
            .custom-reversed-table [class*="rdt_TableRow"]:nth-child(odd):hover {
              background-color: rgba(36, 52, 77, 0.8) !important;
            }
            .custom-reversed-table [class*="rdt_TableCell"] {
              color: #000000 !important;
            }
            .custom-reversed-table div[role="gridcell"] {
              color: #000000 !important;
            }
            .custom-reversed-table [class*="rdt_TableRow"] div {
              color: #000000 !important;
            }
            .custom-reversed-table [class*="rdt_TableRow"] span {
              color: inherit !important;
            }
          `}
        </style>
        {children}
    </div>
);

const Home = () => {

    const axiosWrapper = useAxiosWrapper();
    const { quotenum } = useParams();
    const linkRef = useRef<boolean>();
    const requestIdRef = useRef(0);
    
    const [error, setError] = useRecoilState(errorState);
    const [quotes, setQuotes] = useRecoilState(quotesState);
    const [openOrders, setOpenOrders] = useRecoilState(openOrdersState);
    const [shippedOrders, setShippedOrders] = useRecoilState(shippedOrdersState);
    const [currentQuote, setCurrentQuote] = useRecoilState(currentQuoteState);
    const [user] = useRecoilState(userState);
    
    const [customerPO, setCustomerPO] = useState('');
    const [qNumber, setQNumber] = useState('');
    const [bQuoteSearch, setBQuoteSearch] = useState(true);
    const [customerCO, setCustomerCO] = useState('');
    const [orderPO, setOrderPO] = useState('');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [linkClicked, setLinkClicked] = useState(false);
    const [loading, setLoading] = useState(false);
    const [rowClicked, setRowClicked] = useState(false);
    const [openResponse, setOpenResponse] = useState(false);
    const [shipResponse, setShipResponse] = useState(false);
    const [showInstructions, setShowInstructions] = useState(false);
    const [selectedQNumber, setSelectedQNumber] = useState<string | null>(null);
    
    linkRef.current = linkClicked;

    // Conditional styling for different table types
    const quotesConditionalStyles: ConditionalStyles<Quote>[] = [
        {
            when: (row) => row.qNumber == selectedQNumber,
            style: {
                backgroundColor: tempcoColors.primary,
                userSelect: 'none',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '15px',
                borderLeft: '6px solid #FFD700',
                boxShadow: '0 4px 16px rgba(237, 28, 36, 0.6), inset 0 0 0 1px rgba(255, 255, 255, 0.2)',
                outline: '2px solid #FFD700',
                outlineOffset: '-2px',
            },
        },
    ];

    const openOrdersConditionalStyles: ConditionalStyles<OpenOrder>[] = [
        {
            when: (row) => parseFloat(row.percentComplete) >= 100,
            style: {
                backgroundColor: "#27ae60",
                color: "white",
                fontWeight: "bold",
            }
        },
        {
            when: (row) => parseFloat(row.percentComplete) >= 75 && parseFloat(row.percentComplete) < 100,
            style: {
                backgroundColor: "#f39c12",
                color: "white",
            }
        },
        {
            when: (row) => parseFloat(row.percentComplete) < 75,
            style: {
                backgroundColor: "#e74c3c",
                color: "white",
            }
        },
        {
            when: (row) => row.backOrderQty > 0,
            style: {
                borderLeft: "4px solid #e67e22",
            }
        },
    ];

    const shippedOrdersConditionalStyles: ConditionalStyles<ShippedOrder>[] = [
        {
            when: (row) => row.carrier?.includes('UPS'),
            style: {
                backgroundColor: "#8b4513",
                color: "white",
            }
        },
        {
            when: (row) => row.carrier?.includes('FED'),
            style: {
                backgroundColor: "#663399",
                color: "white",
            }
        },
        {
            when: (row) => {
                const shipDate = new Date(row.shipDate);
                const today = new Date();
                const diffTime = today.getTime() - shipDate.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                return diffDays <= 7; // Shipped within last week
            },
            style: {
                backgroundColor: "#2ecc71",
                color: "white",
                borderLeft: "4px solid #27ae60",
            }
        },
    ];


    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //  qColumns
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    const qColumns: TableColumn<Quote>[] = [
        {
            name: 'Q Number',
            selector: (row) => row.qNumber,
            sortable: true,
            grow: 1,
            reorder: true
        },
        {
            name: 'Sold Name',
            selector: (row) => row.soldName,
            sortable: true,
            grow: 2,
            reorder: true
        },
        {
            name: 'Customer PO',
            selector: (row) => row.cusPO,
            sortable: true,
            grow: 1,
            reorder: true
        },
        {
            name: 'Part #',
            selector: (row) => row.partNo,
            sortable: true,
            minWidth: '250px',
            grow: 2,
            reorder: true,
            cell: (row) => (
                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', padding: '4px 0', pointerEvents: 'none' }}>
                    {row.partNo}
                </div>
            )
        },
        {
            name: 'Made Date',
            selector: (row) => row.madeDate?.substring(0, 10),
            sortable: true,
            grow: 1,
            reorder: true
        },
    ];


    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //  openColumns
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    const openColumns: TableColumn<OpenOrder>[] = [
        {
            name: 'Order Number',
            selector: (row) => row.orderNo,
            sortable: true,
            grow: 1,
            reorder: true
        },
        {
            name: 'Part Number',
            selector: (row) => row.partNo,
            sortable: true,
            grow: 1,
            reorder: true
        },
        {
            name: 'Due Date',
            selector: (row) => row.dueDate,
            sortable: true,
            width: "150px",
            grow: 1,
            cell: (row) => {
                const dueDate = new Date(row.dueDate);
                const today = new Date();
                const diffTime = dueDate.getTime() - today.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                
                let color = '#2c3e50';
                if (diffDays < 0) color = '#e74c3c'; // Overdue
                else if (diffDays <= 7) color = '#f39c12'; // Due soon
                else if (diffDays <= 30) color = '#3498db'; // Due this month
                
                return (
                    <span style={{
                        color: color,
                        fontWeight: diffDays <= 7 ? 'bold' : 'normal',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        backgroundColor: diffDays < 0 ? 'rgba(231, 76, 60, 0.1)' : 
                                        diffDays <= 7 ? 'rgba(243, 156, 18, 0.1)' : 'transparent'
                    }}>
                        {row.dueDate}
                    </span>
                );
            }
        },
        {
            name: 'Quantity',
            selector: (row) => row.qty,
            sortable: true,
            grow: 1,
            reorder: true
        },
        {
            name: 'Back Order Qty',
            selector: (row) => row.backOrderQty,
            sortable: true,
            grow: 1,
            reorder: true
        },
        {
            name: 'M Number',
            selector: (row) => row.mNumber,
            sortable: true,
            grow: 1,
            reorder: true,
            omit: true
        },
        {
            name: 'Percent Complete',
            selector: (row) => parseFloat(row.percentComplete),
            sortable: true,
            grow: 1,
            reorder: true,
            cell: (row) => {
                const raw = row.percentComplete;
                const isEmpty = raw == null || raw === '';
                if (isEmpty) {
                    return (
                        <span style={{ fontSize: '12px', fontStyle: 'italic', opacity: 0.6 }}>
                            N/A
                        </span>
                    );
                }
                const pct = Number(raw) || 0;
                const barColor = pct >= 100 ? '#27ae60' :
                    pct >= 75 ? '#f39c12' : '#e74c3c';
                return (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <div style={{
                        width: '60px',
                        height: '8px',
                        backgroundColor: '#ecf0f1',
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${pct}%`,
                            height: '100%',
                            backgroundColor: barColor,
                            transition: 'width 0.3s ease'
                        }} />
                    </div>
                    <span style={{
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: barColor
                    }}>
                        {raw}%
                    </span>
                </div>
                );
            }
        },

    ];


    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //  shippedColumns
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    const shippedColumns: TableColumn<ShippedOrder>[] = [
        {
            name: 'Order Number',
            selector: (row) => row.orderNo,
            sortable: true,
            grow: 1,
            reorder: true
        },
        {
            name: 'Part Number',
            selector: (row) => row.partNo,
            sortable: true,
            grow: 1,
            reorder: true
        },
        {
            name: 'Ship Date',
            selector: (row) => row.shipDate,
            grow: 1,
            reorder: true,
            cell: (row) => {
                const shipDate = new Date(row.shipDate);
                const today = new Date();
                const diffTime = today.getTime() - shipDate.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                const isRecent = diffDays <= 7;
                
                return (
                    <span style={{
                        color: isRecent ? '#27ae60' : '#2c3e50',
                        fontWeight: isRecent ? 'bold' : 'normal',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        backgroundColor: isRecent ? 'rgba(39, 174, 96, 0.1)' : 'transparent',
                        border: isRecent ? '1px solid rgba(39, 174, 96, 0.3)' : 'none'
                    }}>
                        {row.shipDate}
                        {isRecent && <span style={{ marginLeft: '4px', fontSize: '10px' }}>📦</span>}
                    </span>
                );
            }
        },
        {
            name: 'Ship Quantity',
            selector: (row) => row.shipQty,
            sortable: true,
            grow: 1,
            reorder: true,
        },
        {
            name: 'Carrier',
            selector: (row) => row.carrier,
            sortable: true,
            grow: 1,
            reorder: true,
            cell: (row) => (
                <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    backgroundColor: row.carrier?.includes('UPS') ? '#8b4513' : 
                                   row.carrier?.includes('FED') ? '#663399' : '#7f8c8d',
                    color: 'white',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    {row.carrier}
                </span>
            )
        },
        {
            name: 'Tracking Number',
            selector: (row) => row.trackingNo,
            sortable: true,
            grow: 1,
            reorder: true,
            cell: (row) => <HyperL row={row}>Location</HyperL>,
        },
    ];


    useEffect(() => {
        setLinkClicked(false);
        setError({ isError: false, status: 'n/a', message: 'n/a', requestSent: false });
    }, [setError]);


    const handleSearchBtnClick = () => {
        if (!user?.custNo || user.custNo.length === 0) {
            console.error('User not properly loaded or missing custNo');
            setError({
                isError: true,
                status: 'Error',
                message: 'No customer numbers associated with your account. Please contact support.',
                requestSent: false,
            });
            return;
        }

        setQuotes([]);
        setOpenOrders([]);
        setShippedOrders([]);
        setOpenResponse(false);
        setShipResponse(false);
        setRowClicked(false);
        setSelectedQNumber(null);
        setLoading(true);
        setError({ isError: false, status: 'n/a', message: 'n/a', requestSent: false });

        const custNoArray = user.custNo; // Now an array of customer numbers

        // format a Date the way the backend queries expect (YYYY-M-D)
        const fmtDate = (d: Date) => d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
        const hasDateRange = startDate != null && endDate != null;

        // keep only rows whose date string falls inside the selected range
        const inDateRange = (dateStr: string) => {
            if (!hasDateRange) return true;
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return true;
            return d >= startDate! && d <= endDate!;
        };

        if (!bQuoteSearch) {
            const custCO = customerCO.trim();
            const custPO = orderPO.trim();

            if (custCO == '' && custPO == '') {
                setLoading(false);
                setError({
                    isError: true,
                    status: 'Missing Input',
                    message: 'Enter a Customer CO number or a Customer PO number to search orders.',
                    requestSent: false,
                });
                return;
            }

            // CO number wins if both are filled in; otherwise search by customer PO
            const search = custCO != ''
                ? axiosWrapper.getOpenOrdersCO(custCO)
                : axiosWrapper.getOpenOrdersByPO(custPO);

            search.then(({ openOrders, shippedOrders, canceled }) => {
                if (canceled) return;
                setLoading(false);
                const open = (openOrders || []).filter((o) => inDateRange(o.dueDate));
                const shipped = (shippedOrders || []).filter((s) => inDateRange(s.shipDate));
                setOpenOrders(open);
                setShippedOrders(shipped);
                setOpenResponse(true);
                setShipResponse(true);
                if (open.length == 0 && shipped.length == 0) {
                    setError({
                        isError: true,
                        status: 'No Results',
                        message: 'No orders found for the specified search criteria.',
                        requestSent: false,
                    });
                }
            }).catch((error) => {
                setLoading(false);
                console.error('Search error:', error);
                setError({
                    isError: true,
                    status: 'Search Error',
                    message: 'An error occurred while searching for orders. Please try again.',
                    requestSent: false,
                });
            });
            return;
        }

        const custPO = customerPO.trim();
        const soldName = '';


        axiosWrapper.getQuotes(
            soldName, custPO, custNoArray, qNumber.trim(),
            hasDateRange ? fmtDate(startDate!) : '',
            hasDateRange ? fmtDate(endDate!) : ''
        ).then((data) => {
            setLoading(false);
            if (data?.length > 0) {
                setQuotes(data);
            } else {
                setQuotes([]);
                setError({
                    isError: true,
                    status: 'No Results',
                    message: 'No quotes found for the specified search criteria. Please try a different Customer PO number or leave the field empty to search all quotes.',
                    requestSent: false,
                });
            }
        }).catch((error) => {
            setLoading(false);
            console.error('Search error:', error);
            setError({
                isError: true,
                status: 'Search Error',
                message: 'An error occurred while searching for quotes. Please try again.',
                requestSent: false,
            });
        });
    };


    const handleModeChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        setBQuoteSearch(e.target.value == 'quote');

        // leftover results from the other mode would be misleading — start clean
        setQuotes([]);
        setOpenOrders([]);
        setShippedOrders([]);
        setOpenResponse(false);
        setShipResponse(false);
        setRowClicked(false);
        setSelectedQNumber(null);
        setError({ isError: false, status: 'n/a', message: 'n/a', requestSent: false });
    };


    const handleCOChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        setCustomerCO(e.target.value.toUpperCase().trim());
    };


    const handleOrderPOChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        setOrderPO(e.target.value.toUpperCase().trim());
    };


    const handleDateChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
    };


    const handlePOChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const po = e.target.value.toUpperCase();
        setCustomerPO(po);
        setCurrentQuote({ ...currentQuote, cusPO: po });
    };


    const handleQRowClicked = (row: Quote, e: React.MouseEvent<Element>) => {
        const requestId = ++requestIdRef.current;

        setOpenResponse(false);
        setShipResponse(false);

        setOpenOrders([]);
        setShippedOrders([]);

        setSelectedQNumber(row.qNumber);
        setRowClicked(true);

        axiosWrapper.getOpenOrdersByQ(row.qNumber).then(({ openOrders, shippedOrders, canceled }) => {
            if (canceled || requestId != requestIdRef.current) return;
            setOpenOrders(openOrders || []);
            setShippedOrders(shippedOrders || []);
            setOpenResponse(true);
            setShipResponse(true);
        });
    }


    const openTrackingPage = (trackingno: string, carrier: string) => {

        if (carrier.includes('UPS')) {
            window.open(`https://wwwapps.ups.com/tracking/tracking.cgi?tracknum=${trackingno}`, '_blank');
        } else if (carrier.includes('FED')) {
            window.open(`https://www.fedex.com/fedextrack/?trknbr=${trackingno}`, '_blank');
        }
    };


    const HyperL: FC<{ children: React.ReactNode; row: ShippedOrder }> = ({ children, row }) => {
        if (row.carrier.includes('UPS') || row.carrier.includes('FED')) {
            return (
                <button
                    type='button'
                    onClick={() => openTrackingPage(row.trackingNo, row.carrier)}
                style={{
                    color: '#3498db',
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(52, 152, 219, 0.1)',
                    border: '1px solid rgba(52, 152, 219, 0.3)',
                    transition: 'all 0.2s ease',
                    display: 'inline-block',
                    cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(52, 152, 219, 0.5)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.1)';
                    e.currentTarget.style.borderColor = 'rgba(52, 152, 219, 0.3)';
                    e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                    {row.trackingNo}
                </button>
            );
        } else {
            return <div>{row.trackingNo}</div>;
        }
    };


    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //  REACT OBJECT
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    return (
        <Box display="flex"
             flexDirection="column"
             sx={{
                 background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #333333 100%)',
                 color: 'white',
                 py: 3,
                 px: 3,
                 minHeight: 'calc(100vh - 280px)' // Account for navbar (80px) + footer (200px)
             }}
        >
            {/* Page Header with Tempco Branding */}
            <Box sx={{ mb: 2, textAlign: 'center' }}>
                <TempcoHeading variant="h4" sx={{ mb: 1 }}>
                    Order Tracking Dashboard
                </TempcoHeading>
                <TempcoBodyText variant="body1" sx={{ opacity: 0.8 }}>
                    Order management for {user?.username || 'valued customers'}
                </TempcoBodyText>
            </Box>

            {/* Error Display with Enhanced Tempco Alert */}
            {error.isError && (
                <Box sx={{ mb: 2, mx: 3 }}>
                    <TempcoAlert severity="error">
                        <strong>System Alert:</strong> {error.message}
                    </TempcoAlert>
                </Box>
            )}

            <Box flex={1} overflow="auto">
                <TempcoCard 
                    className="pr-3 pl-3 pt-3"
                    sx={{
                        marginLeft: 3,
                        marginRight: 3,
                        marginBottom: 3,
                        overflow: 'visible',
                    }}
                >
                    <Box
                        sx={{
                            '& .super-app-theme--header': {
                                backgroundColor: 'rgba(5, 60, 200, .6)',
                                fontSize: '10pt',
                            },
                            '& .MuiDataGrid-cell': {
                                fontSize: '9pt',
                            },
                            marginLeft: 5,
                            marginRight: 5,
                            //maxWidth: '820px',
                        }}
                    >
                        {/* Enhanced Search Controls with Tempco Branding */}
                        <TempcoContainer sx={{ mb: 3, p: 3 }}>
                            <Box sx={{ mb: 2 }}>
                                <TempcoGradientText variant="h4" component="h3">
                                    Order Search
                                </TempcoGradientText>
                            </Box>

                            {/* Mode selector: search quotes first, or jump straight to orders */}
                            <RadioGroup
                                row
                                value={bQuoteSearch ? 'quote' : 'order'}
                                onChange={handleModeChange}
                                sx={{
                                    mb: 1,
                                    '& .MuiFormControlLabel-label': { color: 'white', fontWeight: 600 },
                                    '& .MuiRadio-root': { color: 'rgba(255,255,255,0.7)' },
                                    '& .Mui-checked': { color: tempcoColors.primary },
                                }}
                            >
                                <FormControlLabel value="quote" control={<Radio />} label="Search by Quote" />
                                <FormControlLabel value="order" control={<Radio />} label="Search by Order" />
                            </RadioGroup>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', opacity: bQuoteSearch ? 1 : 0.4, transition: 'opacity 0.2s ease' }}>
                                <TempcoTextField
                                    margin="dense"
                                    id="custpo"
                                    label="Customer PO Number"
                                    variant="outlined"
                                    onChange={handlePOChange}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearchBtnClick(); }}
                                    value={customerPO}
                                    inputProps={{style: {fontSize: 16}}}
                                    InputLabelProps={{style: {fontSize: 14}}}
                                    sx={{
                                        minWidth: '320px',
                                        maxWidth: '320px',
                                    }}
                                    placeholder="Enter your Customer PO (optional)"
                                    disabled={!bQuoteSearch}
                                />

                                <TempcoTextField
                                    margin="dense"
                                    id="qnumber"
                                    label="Q Number"
                                    variant="outlined"
                                    onChange={(e) => setQNumber(e.target.value.toUpperCase())}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearchBtnClick(); }}
                                    value={qNumber}
                                    inputProps={{style: {fontSize: 16}}}
                                    InputLabelProps={{style: {fontSize: 14}}}
                                    sx={{
                                        minWidth: '220px',
                                        maxWidth: '220px',
                                    }}
                                    placeholder="Enter Q Number (optional)"
                                    disabled={!bQuoteSearch}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', opacity: bQuoteSearch ? 0.4 : 1, transition: 'opacity 0.2s ease' }}>
                                <TempcoTextField
                                    margin="dense"
                                    id="custco"
                                    label="Customer CO Number"
                                    variant="outlined"
                                    onChange={handleCOChange}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearchBtnClick(); }}
                                    value={customerCO}
                                    inputProps={{style: {fontSize: 16}}}
                                    InputLabelProps={{style: {fontSize: 14}}}
                                    sx={{
                                        minWidth: '320px',
                                        maxWidth: '320px',
                                    }}
                                    placeholder="Enter Customer CO number"
                                    disabled={bQuoteSearch}
                                />

                                <TempcoTextField
                                    margin="dense"
                                    id="orderpo"
                                    label="Customer PO Number"
                                    variant="outlined"
                                    onChange={handleOrderPOChange}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearchBtnClick(); }}
                                    value={orderPO}
                                    inputProps={{style: {fontSize: 16}}}
                                    InputLabelProps={{style: {fontSize: 14}}}
                                    sx={{
                                        minWidth: '320px',
                                        maxWidth: '320px',
                                    }}
                                    placeholder="Or enter your Customer PO"
                                    disabled={bQuoteSearch}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                                <Box>
                                    <TempcoBodyText variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.8 }}>
                                        Date Range (optional)
                                    </TempcoBodyText>
                                    <DatePicker
                                        selected={startDate}
                                        onChange={handleDateChange}
                                        startDate={startDate}
                                        endDate={endDate}
                                        selectsRange
                                        showIcon
                                        isClearable
                                        placeholderText="All dates"
                                        portalId="datepicker-portal"
                                    />
                                </Box>

                                <TempcoButton
                                    variant="contained"
                                    onClick={handleSearchBtnClick}
                                    disabled={loading}
                                    sx={{
                                        minWidth: '120px',
                                        fontWeight: 'bold',
                                        minHeight: '56px',
                                        fontSize: '1rem',
                                        px: 3,
                                    }}
                                >
                                    {loading ? 'Searching...' : `Search ${quotenum || 'Orders'}`}
                                </TempcoButton>
                                
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={() => setShowInstructions(true)}
                                    sx={{
                                        color: tempcoColors.primary,
                                        borderColor: tempcoColors.primary,
                                        minHeight: '56px',
                                        fontWeight: 600,
                                        px: 2,
                                        background: 'rgba(237, 28, 36, 0.05)',
                                        '&:hover': {
                                            borderColor: tempcoColors.primary,
                                            backgroundColor: 'rgba(237, 28, 36, 0.1)',
                                            color: tempcoColors.light,
                                        }
                                    }}
                                >
                                    ❓ Instructions
                                </Button>
                            </Box>


                        </TempcoContainer>

                        {/* Instructions Popup Dialog */}
                        <Dialog
                            open={showInstructions}
                            onClose={() => setShowInstructions(false)}
                            maxWidth="sm"
                            fullWidth
                            PaperProps={{
                                sx: {
                                    bgcolor: 'rgba(68, 68, 68, 0.95)',
                                    borderRadius: 2,
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                                    border: '1px solid rgba(237, 28, 36, 0.2)',
                                }
                            }}
                        >
                            <DialogTitle sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                bgcolor: tempcoColors.primary, 
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '18px',
                                fontFamily: '"Oswald", "Arial Black", "Arial", sans-serif'
                            }}>
                                <Box sx={{ 
                                    width: 28, 
                                    height: 28, 
                                    borderRadius: '50%', 
                                    bgcolor: 'white', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    mr: 2
                                }}>
                                    <span style={{ color: tempcoColors.primary, fontSize: '16px', fontWeight: 'bold' }}>ℹ</span>
                                </Box>
                                How to Search for Orders
                            </DialogTitle>
                            <DialogContent sx={{ pt: 3, pb: 2 }}>
                                <Box sx={{ color: tempcoColors.white }}>
                                    <div style={{ 
                                        marginBottom: '12px', 
                                        marginTop: '16px',
                                        fontSize: '16px', 
                                        fontWeight: '600',
                                        color: tempcoColors.white,
                                        fontFamily: '"Lato", "Arial", "Helvetica", sans-serif'
                                    }}>
                                        <strong>1.</strong> Enter your Customer PO number in the text field below
                                    </div>
                                    <div style={{ 
                                        marginBottom: '12px', 
                                        fontSize: '16px', 
                                        fontWeight: '600',
                                        color: tempcoColors.white,
                                        fontFamily: '"Lato", "Arial", "Helvetica", sans-serif'
                                    }}>
                                        <strong>2.</strong> Click the "Search" button to find related quotes
                                    </div>
                                    <div style={{ 
                                        marginBottom: '16px', 
                                        fontSize: '16px', 
                                        fontWeight: '600',
                                        color: tempcoColors.white,
                                        fontFamily: '"Lato", "Arial", "Helvetica", sans-serif'
                                    }}>
                                        <strong>3.</strong> Click on any quote row to view open orders and shipped orders
                                    </div>
                                    <div style={{
                                        marginBottom: '16px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: tempcoColors.white,
                                        fontFamily: '"Lato", "Arial", "Helvetica", sans-serif'
                                    }}>
                                        <strong>4.</strong> Or choose "Search by Order" to skip quotes and find orders directly by Customer CO or Customer PO number. A date range can narrow either search.
                                    </div>
                                    <Box sx={{ 
                                        p: 2, 
                                        bgcolor: 'rgba(237, 28, 36, 0.15)', 
                                        borderRadius: 1, 
                                        border: '1px solid rgba(237, 28, 36, 0.5)',
                                        mt: 2
                                    }}>
                                        <div style={{ 
                                            fontSize: '15px', 
                                            fontWeight: '600', 
                                            color: tempcoColors.light,
                                            fontFamily: '"Lato", "Arial", "Helvetica", sans-serif'
                                        }}>
                                            💡 <strong>Helpful Tip:</strong> Leave the Customer PO field empty to search all quotes for your account
                                        </div>
                                    </Box>
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ p: 2 }}>
                                <TempcoButton 
                                    onClick={() => setShowInstructions(false)}
                                    variant="contained"
                                    sx={{
                                        fontWeight: 'bold',
                                        px: 3
                                    }}
                                >
                                    Got it!
                                </TempcoButton>
                            </DialogActions>
                        </Dialog>

                        {/* /////////////////////////////////////////////////////////////////// */}
                        {/* //  QUOTES TABLE */}
                        {/* /////////////////////////////////////////////////////////////////// */}
                        {bQuoteSearch &&
                            <StyledDataTableWrapper>
                                <ResizableTable
                                    tableId="quotes-table"
                                    keyField="qNumber"
                                    initialWidth={1100}
                                    minWidth={600}
                                    maxWidth={1800}
                                    columns={qColumns}
                                    data={quotes}
                                    title='Quotes'
                                    pagination
                                    onRowClicked={handleQRowClicked}
                                    progressPending={loading}
                                    paginationPerPage={20}
                                    theme="reversedDark"
                                    conditionalRowStyles={quotesConditionalStyles}
                                    highlightOnHover
                                    pointerOnHover
                                    responsive
                                    striped
                                />
                            </StyledDataTableWrapper>
                        }
                        {(rowClicked && !openResponse) &&
                            <Box sx={{ mb: 2 }}>
                                <LinearProgress/>
                            </Box>
                        }


                        {/* /////////////////////////////////////////////////////////////////// */}
                        {/* //  OPEN ORDERS TABLE */}
                        {/* /////////////////////////////////////////////////////////////////// */}
                        {openResponse &&
                            <StyledDataTableWrapper>
                                <ResizableTable
                                    tableId="open-orders-table"
                                    initialWidth={1100}
                                    minWidth={600}
                                    maxWidth={1800}
                                    columns={openColumns}
                                    data={openOrders}
                                    title='Open Orders'
                                    pagination
                                    paginationPerPage={10}
                                    theme="reversedDark"
                                    highlightOnHover
                                    responsive
                                    striped
                                    conditionalRowStyles={openOrdersConditionalStyles}
                                />
                            </StyledDataTableWrapper>
                        }
                        {(rowClicked && !shipResponse) &&
                            <Box sx={{ mb: 2 }}>
                                <LinearProgress/>
                            </Box>
                        }

                        {/* /////////////////////////////////////////////////////////////////// */}
                        {/* //  SHIPPED ORDERS TABLE */}
                        {/* /////////////////////////////////////////////////////////////////// */}
                        {shipResponse &&
                            <StyledDataTableWrapper>
                                <ResizableTable
                                    tableId="shipped-orders-table"
                                    initialWidth={1100}
                                    minWidth={600}
                                    maxWidth={1800}
                                    columns={shippedColumns}
                                    data={shippedOrders}
                                    title='Shipped Orders'
                                    pagination
                                    paginationPerPage={10}
                                    theme="reversedDark"
                                    highlightOnHover
                                    responsive
                                    striped
                                    conditionalRowStyles={shippedOrdersConditionalStyles}
                                />
                            </StyledDataTableWrapper>
                        }
                    </Box>
                </TempcoCard>
            </Box>
            <IdolTimerWrapper/>
        </Box>

    );
}


export { Home };
